/* =========================================================================
   Auth + history UI controller. Wires the welcome / login / register / history
   screens to the API client. Every server path degrades gracefully: if the API
   is unreachable the player can always continue as guest (local packs).
   ========================================================================= */
import { byId, show } from './dom';
import { wirePasswordReveals, revealTheo } from './ui/authVisuals';
import { refreshModeSwitcher } from './ui/modeSwitcher';
import { renderHistory, summaryLine } from './ui/historyView';
import * as api from './api';
import { flushPendingSummary } from './pendingSummary';
import { track } from './telemetry';
import { consumePostAuthResume } from './postAuthResume';
import { renderArcade } from './arcade';
import { recallGtky } from './characters/memory';
import { isRealName, sanitizeName, PLAYER_NAME_KEY } from './characters/name';
import { setAuthedHistory, authedWithHistoryFromStats } from './progression';
import { setGtkyEngagement } from './gtkyCadence';
import { refreshHomeSurfaces } from './homeGate';

let lastScreenBeforeHistory = 'home';

export function showWelcome(): void {
  byId('bankbar').classList.add('hidden');
  show('welcome');
  void loadAppleConfig(); // non-blocking: reveal the Apple button only if enabled
  // Non-blocking reachability probe → show a friendly offline banner if the API is down.
  void api.pingApi().then((up) => {
    const banner = byId('welcomeOffline');
    if (up) {
      banner.classList.add('hidden');
    } else {
      banner.textContent = 'Offline. You can still Play as Guest. Sign in when you are back online to sync progress.';
      banner.classList.remove('hidden');
    }
  });
}

export function showHome(): void {
  byId('bankbar').classList.add('hidden');
  show('home');
  renderArcade(); // refresh the Arcade section's locked/unlocked state
  refreshModeSwitcher(); // ...and the lockup's mode dropdown, same gates
}

export function showLogin(prefillEmail = ''): void {
  setMsg('loginMsg', '', '');
  (byId('loginForm') as HTMLFormElement).reset();
  if (prefillEmail) {
    (byId('loginEmail') as HTMLInputElement).value = prefillEmail;
    setMsg('loginMsg', 'Welcome back. Enter your password.', 'info');
  }
  show('login');
  byId<HTMLInputElement>(prefillEmail ? 'loginPassword' : 'loginEmail').focus();
}

export function showRegister(prefillEmail = ''): void {
  setMsg('registerMsg', '', '');
  (byId('registerForm') as HTMLFormElement).reset();
  for (const f of AVAIL_FIELDS) setAvail(f, '', '');
  if (prefillEmail) {
    (byId('regEmail') as HTMLInputElement).value = prefillEmail;
    setMsg('registerMsg', 'New here. Pick a player name.', 'info');
  }
  // Conversion bridge: if the player already told Theo their first name as a
  // guest, prefill the display-name field with it (they can still change it).
  const guestName = recallGtky(PLAYER_NAME_KEY);
  if (isRealName(guestName)) {
    (byId('regName') as HTMLInputElement).value = sanitizeName(guestName as string);
  }
  show('register');
  byId<HTMLInputElement>('regName').focus();
}

/* ---- identifier-first Continue (welcome screen): route by email existence ---- */
async function handleWelcomeContinue(e: Event): Promise<void> {
  e.preventDefault();
  const email = (byId('welcomeEmail') as HTMLInputElement).value.trim();
  if (!/.+@.+\..+/.test(email)) {
    setMsg('welcomeMsg', 'Enter a valid email to continue.', 'err');
    return;
  }
  setMsg('welcomeMsg', '', '');
  const btn = byId<HTMLButtonElement>('welcomeContinue');
  btn.disabled = true;
  const r = await api.checkAvailability(undefined, email);
  btn.disabled = false;
  if (r.ok && r.data.emailAvailable === false) showLogin(email);       // known → sign in
  else showRegister(email);                                            // new (or check unavailable) → create
}

/** After sign-in/registration: if a mid-game resume is armed (guest tapped
    "Create free account" during a run), pick the game back up exactly where it
    left off — there is no pending summary mid-run. Otherwise, if a guest game's
    tally is waiting, save it and return the player to the results screen so they
    see it land; else go home. */
async function afterAuthSuccess(): Promise<void> {
  // Identity changed: resolve server history for the disclosure tier (existing
  // accounts are never re-gated), then refresh every identity-sensitive home
  // surface — the greeting throttle would otherwise hold the pre-auth render.
  if (api.isAuthenticated()) {
    const pr = await api.getProfileMe();
    if (pr.ok) {
      setAuthedHistory(authedWithHistoryFromStats(pr.data.stats));
      setGtkyEngagement(pr.data.stats); // GTKY engaged-player gate (§6)
    }
  }
  refreshHomeSurfaces();

  const resume = consumePostAuthResume();
  if (resume) {
    resume();
    return;
  }
  const saved = await flushPendingSummary();
  if (!saved) {
    showHome();
    return;
  }
  track('PendingSummarySaved');
  byId('resGuestCtaText').classList.add('hidden');
  byId('resSaveScoreBtn').classList.add('hidden');
  byId('resGuestSaved').classList.remove('hidden');
  byId('resultsHistoryBtn').classList.remove('hidden');
  show('results');
}

function setMsg(id: string, text: string, kind: 'err' | 'ok' | 'info' | ''): void {
  const el = byId(id);
  el.textContent = text;
  el.className = 'auth-msg' + (kind ? ' ' + kind : '');
}

async function handleLogin(e: Event): Promise<void> {
  e.preventDefault();
  const email = (byId('loginEmail') as HTMLInputElement).value.trim();
  const password = (byId('loginPassword') as HTMLInputElement).value;
  if (!email || !password) {
    setMsg('loginMsg', 'Enter your email and password.', 'err');
    return;
  }
  const btn = byId<HTMLButtonElement>('loginSubmit');
  btn.disabled = true;
  setMsg('loginMsg', 'Signing in…', 'info');
  const r = await api.login(email, password);
  btn.disabled = false;
  if (r.ok) {
    setMsg('loginMsg', 'Welcome back.', 'ok');
    await afterAuthSuccess();
  } else if (r.offline) {
    setMsg('loginMsg', 'Server unavailable. You can Play as Guest for now.', 'err');
  } else {
    setMsg('loginMsg', r.error || 'Sign in failed.', 'err');
  }
}

/* ---- live availability check (register screen ✓/✗ as you type) ---- */
type AvailField = { input: string; badge: string; hint: string; kind: 'displayName' | 'email' };
const AVAIL_FIELDS: AvailField[] = [
  { input: 'regName', badge: 'regNameAvail', hint: 'regNameHint', kind: 'displayName' },
  { input: 'regEmail', badge: 'regEmailAvail', hint: 'regEmailHint', kind: 'email' },
];
let availTimer: ReturnType<typeof setTimeout> | null = null;

function wireAvailability(): void {
  for (const f of AVAIL_FIELDS) {
    byId<HTMLInputElement>(f.input).addEventListener('input', () => {
      setAvail(f, '', '');                       // clear while typing
      if (availTimer) clearTimeout(availTimer);
      availTimer = setTimeout(() => void runAvailCheck(f), 400);   // debounce
    });
  }
}

async function runAvailCheck(f: AvailField): Promise<void> {
  const value = byId<HTMLInputElement>(f.input).value.trim();
  if (f.kind === 'displayName' && value.length < 5) return; // social-app rule: 5–20 chars
  if (f.kind === 'email' && !/.+@.+\..+/.test(value)) return;
  setAvail(f, '…', '');
  const r = await api.checkAvailability(
    f.kind === 'displayName' ? value : undefined,
    f.kind === 'email' ? value : undefined,
  );
  // ignore stale responses: only apply if the field still holds the checked value
  if (byId<HTMLInputElement>(f.input).value.trim() !== value) return;
  if (!r.ok) { setAvail(f, '', ''); return; }    // offline → no indicator; server re-validates
  const ok = f.kind === 'displayName' ? r.data.displayNameAvailable : r.data.emailAvailable;
  if (ok === true) setAvail(f, '✓', f.kind === 'displayName' ? 'Available' : '', 'ok');
  else if (ok === false) setAvail(f, '✗', f.kind === 'displayName' ? 'That name is taken.' : 'Already registered. Try signing in.', 'err');
}

function setAvail(f: AvailField, mark: string, hint: string, cls: 'ok' | 'err' | '' = ''): void {
  const badge = byId(f.badge);
  badge.textContent = mark;
  badge.className = 'avail' + (cls ? ' ' + cls : '');
  const h = byId(f.hint);
  h.textContent = hint;
  h.className = 'avail-hint' + (cls ? ' ' + cls : '');
}

async function handleRegister(e: Event): Promise<void> {
  e.preventDefault();
  const displayName = (byId('regName') as HTMLInputElement).value.trim();
  const email = (byId('regEmail') as HTMLInputElement).value.trim();
  const password = (byId('regPassword') as HTMLInputElement).value;
  if (displayName.length < 5 || displayName.length > 20) {
    setMsg('registerMsg', 'Player name must be 5–20 characters.', 'err');
    return;
  }
  if (!email || password.length < 8) {
    setMsg('registerMsg', 'Email and an 8+ character password are required.', 'err');
    return;
  }
  const btn = byId<HTMLButtonElement>('registerSubmit');
  btn.disabled = true;
  setMsg('registerMsg', 'Creating your account…', 'info');
  const r = await api.register(displayName, email, password);
  btn.disabled = false;
  if (r.ok) {
    setMsg('registerMsg', 'Account created.', 'ok');
    await afterAuthSuccess();
  } else if (r.offline) {
    setMsg('registerMsg', 'Server unavailable. You can Play as Guest for now.', 'err');
  } else {
    setMsg('registerMsg', r.error || 'Registration failed.', 'err');
  }
}

export async function showHistory(from: string = 'home'): Promise<void> {
  lastScreenBeforeHistory = from;
  byId('bankbar').classList.add('hidden'); // keep the top-right account button clear
  show('history');
  const list = byId('historyList');
  const sub = byId('historySub');
  if (!api.isAuthenticated()) {
    sub.textContent = 'Sign in to see your saved sessions.';
    list.innerHTML = `<div class="hist-empty">Guest play isn't saved to a history.<br/>Create an account to keep your bank, medals & streaks.</div>`;
    return;
  }
  sub.textContent = 'Your recent sessions';
  list.innerHTML = `<div class="hist-empty">Loading…</div>`;
  const r = await api.getHistory();
  if (!r.ok) {
    list.innerHTML = `<div class="hist-empty">${r.offline ? 'Offline. History will appear when you reconnect.' : 'Could not load history.'}</div>`;
    return;
  }
  const sessions = r.data.sessions || [];
  if (sessions.length === 0) {
    list.innerHTML = `<div class="hist-empty">No sessions yet. Play a round to get started.</div>`;
    return;
  }
  // ui/historyView does the thinking: hollow rows (started, never answered a
  // question) are hidden and counted in the subtitle; in-progress rows get a
  // Resume affordance; completed rows sit under sticky day labels.
  sub.textContent = summaryLine(sessions);
  list.innerHTML = renderHistory(sessions, new Date());
  // ▶ Resume = the history Play button (wired to startOrResume in main.ts):
  // it picks up the server's active session, which db/051 now makes the
  // richest open game rather than the most recently touched one. Reuse the
  // button rather than importing the game controller.
  list.querySelectorAll<HTMLButtonElement>('[data-hist-resume]').forEach((b) =>
    b.addEventListener('click', () => {
      lastScreenBeforeHistory = 'home';
      byId('historyPlayBtn').click();
    }),
  );
}

/* ==========================================================================
   Sign in with Apple (runtime-gated). The build-time flag is gone: on the
   welcome screen we fetch /api/auth/apple/config and only reveal the button when
   the server reports it enabled. Apple's JS is lazily loaded on first use.
   ========================================================================== */
declare global {
  interface Window {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    AppleID?: any;
    /** iOS-wrapper hook: WKWebView may not support the JS popup, so the native
        shell injects the ASAuthorization identity token straight into this. */
    catholicsAppleToken?: (token: string) => void;
  }
}

let appleConfig: api.AppleConfig | null = null;
let appleReady = false;

/** Non-blocking: reveal the Apple button only if the server has it enabled. */
async function loadAppleConfig(): Promise<void> {
  const btn = byId('appleBtn');
  const r = await api.getAppleConfig();
  if (r.ok && r.data.enabled && r.data.clientId) {
    appleConfig = r.data;
    btn.classList.remove('hidden');
  } else {
    btn.classList.add('hidden'); // disabled or offline → stay hidden
  }
}

function loadAppleScript(): Promise<void> {
  return new Promise((resolve, reject) => {
    if (window.AppleID) return resolve();
    const s = document.createElement('script');
    s.src = 'https://appleid.cdn-apple.com/appleauth/static/jsapi/appleid/1/en_US/appleid.auth.js';
    s.onload = () => resolve();
    s.onerror = () => reject(new Error('Apple JS failed to load'));
    document.head.appendChild(s);
  });
}

async function ensureAppleInit(): Promise<boolean> {
  if (!appleConfig) return false;
  try {
    await loadAppleScript();
  } catch {
    return false;
  }
  if (!appleReady && window.AppleID) {
    window.AppleID.auth.init({
      clientId: appleConfig.clientId,
      scope: 'name email',
      redirectURI: location.origin + '/',
      usePopup: true,
    });
    appleReady = true;
  }
  return appleReady;
}

/** Apple's JS SDK rejects auth.signIn() with an { error: <code> } object for a
    genuine user cancel; anything else (domain/config/network trouble) shows up
    as a different code or a plain Error. Only these codes mean "the person
    backed out" — everything else gets logged with its real reason instead of
    the same blanket "cancelled" message that made every failure look identical
    from the field (Eric, 2026-08-02). */
const APPLE_CANCEL_CODES = new Set(['popup_closed_by_user', 'user_cancelled_authorize', 'user_trigger_new_signin_flow']);

function appleErrorCode(err: unknown): string {
  if (err && typeof err === 'object' && 'error' in err && typeof (err as { error?: unknown }).error === 'string') {
    return (err as { error: string }).error;
  }
  return err instanceof Error ? err.message : String(err);
}

async function handleAppleSignIn(): Promise<void> {
  if (!(await ensureAppleInit())) {
    setMsg('welcomeMsg', 'Apple sign-in is unavailable right now.', 'err');
    track('AppleSignInFailed', { stage: 'init' });
    return;
  }
  setMsg('welcomeMsg', 'Contacting Apple…', 'info');
  try {
    const res = await window.AppleID!.auth.signIn();
    const idToken: string | undefined = res?.authorization?.id_token;
    if (!idToken) {
      setMsg('welcomeMsg', 'Apple sign-in was cancelled.', 'err');
      track('AppleSignInFailed', { stage: 'popup', reason: 'no_id_token' });
      return;
    }
    // Apple returns the user's name ONLY on the first authorization.
    const nm = res?.user?.name;
    const displayName = nm ? [nm.firstName, nm.lastName].filter(Boolean).join(' ').trim() : '';
    await completeAppleSignIn(idToken, displayName || undefined);
  } catch (err) {
    const code = appleErrorCode(err);
    const cancelled = APPLE_CANCEL_CODES.has(code);
    setMsg('welcomeMsg', cancelled ? 'Apple sign-in was cancelled.' : 'Apple sign-in ran into a problem. Please try again.', 'err');
    track('AppleSignInFailed', { stage: 'popup', reason: code });
  }
}

/** Shared completion for both the JS popup and the native-injected token: POST
    the identity token, then run the exact same afterAuthSuccess path as login
    (mid-game resume hook + pendingSummary flush). */
async function completeAppleSignIn(identityToken: string, displayName?: string): Promise<void> {
  const r = await api.appleSignIn(identityToken, displayName);
  if (r.ok) {
    setMsg('welcomeMsg', 'Welcome.', 'ok');
    await afterAuthSuccess();
  } else if (r.offline) {
    setMsg('welcomeMsg', 'Server unavailable. You can Play as Guest for now.', 'err');
    track('AppleSignInFailed', { stage: 'server', reason: 'offline' });
  } else {
    setMsg('welcomeMsg', r.error || 'Apple sign-in failed.', 'err');
    track('AppleSignInFailed', { stage: 'server', status: r.status, reason: r.error || 'unauthorized' });
  }
}

/** Resume a pending mid-game run instead of navigating away (register/login
    "back" + "play as guest" escape hatches must not lose the run). */
function escapeAuthOr(fallback: () => void): void {
  const resume = consumePostAuthResume();
  if (resume) {
    resume();
    return;
  }
  fallback();
}

/** Attach all auth/history navigation handlers. Called once at boot. */
export function wireAuth(onPlay: () => void): void {
  byId('guestBtn').addEventListener('click', showHome);
  byId('welcomeForm').addEventListener('submit', (e) => void handleWelcomeContinue(e));
  byId('appleBtn').addEventListener('click', () => void handleAppleSignIn());
  // Native iOS shell injects the identity token here (popup may fail in WKWebView).
  window.catholicsAppleToken = (token: string) => void completeAppleSignIn(token);

  wirePasswordReveals();  // show/hide on every password field
  revealTheo();           // Brother Theo greets on login + register once his art decodes
  byId('loginBackBtn').addEventListener('click', () => escapeAuthOr(showWelcome));
  byId('loginForm').addEventListener('submit', (e) => void handleLogin(e));
  byId('loginToRegister').addEventListener('click', () => showRegister());
  byId('loginAsGuest').addEventListener('click', () => escapeAuthOr(showHome));

  byId('registerBackBtn').addEventListener('click', () => escapeAuthOr(showWelcome));
  byId('registerForm').addEventListener('submit', (e) => void handleRegister(e));
  byId('registerToLogin').addEventListener('click', () => showLogin());
  byId('registerAsGuest').addEventListener('click', () => escapeAuthOr(showHome));
  wireAvailability();   // live ✓/✗ on display name + email

  byId('historyBackBtn').addEventListener('click', () => show(lastScreenBeforeHistory));
  byId('historyPlayBtn').addEventListener('click', onPlay);
  byId('resultsHistoryBtn').addEventListener('click', () => void showHistory('results'));
  byId('resSaveScoreBtn').addEventListener('click', () => {
    track('GuestResultsCtaClicked');
    byId('bankbar').classList.add('hidden');
    showRegister();
  });
}
